import { useEffect, useState, useCallback } from "react";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";

const STATE_UI = {
  verified: {
    icon: ShieldCheck,
    tone: "border-emerald-200 bg-emerald-50 text-emerald-900",
    title: "Verified business",
    body: "Your business verification is approved. All features are unlocked.",
  },
  grace: {
    icon: Clock,
    tone: "border-amber-200 bg-amber-50 text-amber-900",
    title: "Verification pending — you're in your grace period",
    body: "Complete business verification within your grace window to earn a Verified badge and full access.",
  },
  verification_review_required: {
    icon: ShieldQuestion,
    tone: "border-sky-200 bg-sky-50 text-sky-900",
    title: "Verification review required",
    body: "Your account has temporary access. Submit verification to earn a Verified badge.",
  },
  verification_restricted: {
    icon: ShieldAlert,
    tone: "border-rose-200 bg-rose-50 text-rose-900",
    title: "Account verification-restricted",
    body: "You can edit your profile, upload documents, manage billing and contact support. Matches, directory listing and Deal Room funding are locked until verification is approved.",
  },
  suspended_unverified: {
    icon: ShieldAlert,
    tone: "border-rose-300 bg-rose-100 text-rose-950",
    title: "Account suspended (unverified)",
    body: "Your account is suspended for inactivity while unverified. Contact support to reactivate and complete verification.",
  },
};

export default function VerificationBanner() {
  const [status, setStatus] = useState(null);

  const load = useCallback(() => {
    api.get("/verification/status")
      .then(({ data }) => setStatus(data))
      .catch((e) => logger.warn("load verification status", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!status || status.state === "verified") return null;

  const ui = STATE_UI[status.state] || STATE_UI.verification_review_required;
  const Icon = ui.icon;
  const deadline = status.deadline ? new Date(status.deadline).toLocaleString() : null;

  return (
    <div data-testid="verification-banner" className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 ${ui.tone}`}>
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" />
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p data-testid="verification-state-title" className="text-sm font-semibold">{ui.title}</p>
          <span data-testid="verification-badge"
            className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium uppercase tracking-wide">
            {status.badge}
          </span>
        </div>
        <p className="mt-1 text-sm leading-relaxed opacity-90">{ui.body}</p>
        {deadline && status.state === "grace" && (
          <p data-testid="verification-deadline" className="mt-1 text-xs opacity-80">Deadline: {deadline}</p>
        )}
      </div>
    </div>
  );
}
